import React, { useState } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { Snackbar } from '@material-ui/core';
import { ApolloQueryResult } from 'apollo-client';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import { connect } from 'react-redux';

import {
  createImage,
  createImageVariables,
  getFileUploadUrl,
  getFileUploadUrlVariables,
} from './__queries__';
import { Wrapper } from './styles';
import * as actions from '../../../../actions';
import { userActionLog, userActionTypes } from '../../../../Components/UserActionLog';
import { useDispatch, useI18n } from '../../../../Hooks';
import * as queries from '../../../../queries';
import State from '../../../../types/state';
import DropFile from '../../../DropFile';

const CREATE_IMAGE = gql`
  mutation createImage($data: ImageCreateInput!) {
    createImage(data: $data) {
      id
    }
  }
`;

const FILE_UPLOAD_URL = gql`
  mutation getFileUploadUrl($filename: String!) {
    getFileUploadUrl(filename: $filename) {
      file {
        id
        name
        contentType
        secret
        url
      }
      presignedUrlData
    }
  }
`;

type Refetch = (variables?: Record<string, any> | undefined) => Promise<ApolloQueryResult<any>>;

export interface Props {
  onSuccess?: any;
  companyId?: string;
  client?: any;
}

const ImageDrop = ({ client, companyId, onSuccess }: Props) => {
  const dispatch = useDispatch();
  const [uploadStatus, setUploadStatus] = useState('');
  const [createImageQuery] = useMutation<createImage, createImageVariables>(CREATE_IMAGE);
  const [getFileUploadUrlQuery] = useMutation<getFileUploadUrl, getFileUploadUrlVariables>(
    FILE_UPLOAD_URL,
  );
  const { t } = useI18n();

  const save = async (file) => {
    if (file.size > 5242880) {
      dispatch(actions.showNotice(`${t('General.ImageTooBig')} (${file.name})`));
      return;
    }

    setUploadStatus(`${t('General.Uploading')}...`);
    try {
      const fileInfo = await getFileUploadUrlQuery({
        variables: {
          filename: file.name,
        },
      });

      const newFile = fileInfo?.data?.getFileUploadUrl.file;
      const presignedUrlData = fileInfo?.data?.getFileUploadUrl?.presignedUrlData;

      const formData = new FormData();
      Object.keys(presignedUrlData.fields).forEach((key) => {
        formData.append(key, presignedUrlData.fields[key]);
      });
      formData.append('file', file);

      await fetch(presignedUrlData.url, {
        method: 'POST',
        body: formData,
      });

      const created = await createImageQuery({
        refetchQueries: [
          {
            query: queries.images,
            variables: {
              companyId,
            },
          },
        ],
        variables: {
          data: {
            company: { connect: { id: companyId } },
            title: file.name,
            file: { connect: { id: newFile?.id } },
          },
        },
      });

      const id = created?.data?.createImage?.id;
      userActionLog({ id, type: userActionTypes.UPLOAD }, client);
      setUploadStatus(`${file.name} ${t('General.Uploaded')}`);
      if (onSuccess) {
        onSuccess();
      }
    } catch (savingError) {
      console.error('savingError', savingError);
      setUploadStatus(t('General.UploadFailed'));
    }
  };

  const handleFileDrop = (droppedFiles) => {
    Promise.all(droppedFiles.map((file) => save(file)));
  };

  return (
    <Wrapper>
      <DropFile
        accepted="image/*"
        text={t('General.DropFileText')}
        dropText={t('General.DropFileDropText')}
        multiple
        color="#fff"
        borderColor="#666b71"
        handleFile={(filesToHandle) => handleFileDrop(filesToHandle)}
        height="48px"
        width="192px"
        fontSize={12}
      />
      <Snackbar
        open={!!uploadStatus}
        message={uploadStatus}
        autoHideDuration={4000}
        onClose={() => setUploadStatus('')}
      />
    </Wrapper>
  );
};

const mapStateToProps = (state: State) => ({
  companyId: state.company,
});

export default withApollo(connect(mapStateToProps)(ImageDrop));
